import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { formatTime } from '../utils';
import useAuth from './useAuth';
import usePlayer from './usePlayer';
import type { ListData, ParsedSentence } from '../../../types';

export interface Selection {
  startSeconds: number | null;
  endSeconds: number | null;
}

export interface ItemContextValue {
  content: ListData;
  sentences: ParsedSentence[];
  timeMap: number[];
  selection: Selection;
  setSelection: (
    startSeconds: number | null,
    endSeconds: number | null
  ) => void;
  clearSelection: () => void;
  hasSelection: boolean;
  loading: boolean;
  loadingMore: boolean;
  // Playlist-specific fields
  isPlaylist: boolean;
  playlistItems: ListData[];
  currentPosition: number;
  currentItem: ListData | null;
  setPlaylistPosition: (position: number) => void;
}

const ItemContext = createContext<ItemContextValue | null>(null);

export function useItem(): ItemContextValue {
  const context = useContext(ItemContext);
  if (!context) {
    throw new Error('useItem must be used within an ItemProvider');
  }
  return context;
}

const FETCH_BATCH = 100;
const TRANSCRIPT_POLL_INTERVAL_MS = 1000;
const TRANSCRIPT_POLL_COOLDOWN_MS = 750;

// Parse mm:ss format to seconds
const parseTimeStr = (str: string | undefined): number | null => {
  if (!str) return null;
  const match = str.trim().match(/^(\d+):(\d{2})$/);
  if (!match || !match[1] || !match[2]) return null;
  const minutes = parseInt(match[1], 10);
  const seconds = parseInt(match[2], 10);
  if (seconds >= 60) return null;
  return minutes * 60 + seconds;
};

// Parse selection from URL param
const parseSelectionParam = (sel: string | null): Selection => {
  if (!sel) return { startSeconds: null, endSeconds: null };
  // Find the dash that separates start and end times (after the first colon)
  const dashIdx = sel.indexOf('-', sel.indexOf(':') + 1);
  if (dashIdx === -1) return { startSeconds: null, endSeconds: null };
  const startSeconds = parseTimeStr(sel.slice(0, dashIdx));
  const endSeconds = parseTimeStr(sel.slice(dashIdx + 1));
  if (startSeconds === null || endSeconds === null) {
    return { startSeconds: null, endSeconds: null };
  }
  return { startSeconds, endSeconds };
};

interface ItemProviderProps {
  children: React.ReactNode;
}

export function ItemProvider({ children }: ItemProviderProps) {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading: authLoading } = useAuth();
  const {
    nowPlayingId,
    currentTime,
    isPlaying,
    currentPlaylist,
    playPlaylist,
  } = usePlayer();

  // === Selection state (useState, synced to URL) ===
  const [selection, setSelectionState] = useState<Selection>(() =>
    parseSelectionParam(searchParams.get('selected'))
  );

  // Update URL when selection changes
  const setSelection = useCallback(
    (startSeconds: number | null, endSeconds: number | null) => {
      setSelectionState({ startSeconds, endSeconds });
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          // Always clear search query when setting selection
          params.delete('q');
          if (startSeconds === null || endSeconds === null) {
            params.delete('selected');
          } else {
            const start = formatTime(Math.floor(startSeconds));
            const end = formatTime(Math.floor(endSeconds));
            params.set('selected', `${start}-${end}`);
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearSelection = useCallback(() => {
    setSelection(null, null);
  }, [setSelection]);

  const hasSelection =
    selection.startSeconds !== null && selection.endSeconds !== null;

  // === Content data ===
  const [content, setContent] = useState<ListData | null>(null);
  const [sentences, setSentences] = useState<ParsedSentence[]>([]);
  const [timeMap, setTimeMap] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sentencesFetched, setSentencesFetched] = useState(false);
  const prevStatusRef = useRef<string | null>(null);
  const sentencesRef = useRef<ParsedSentence[]>([]);
  const prevWordRefreshStatusRef = useRef<string | null>(null);

  // === Playlist state ===
  const [playlistItems, setPlaylistItems] = useState<ListData[]>([]);
  const isPlaylist = content?.type === 'playlist';

  // Current position in playlist - use player's position if this is the active playlist
  const currentPosition =
    currentPlaylist?.id === content?.id
      ? (currentPlaylist?.playlistPosition ?? 0)
      : (content?.playlistPosition ?? 0);

  const currentItem = isPlaylist
    ? (playlistItems[currentPosition] ?? null)
    : null;

  // The content ID to fetch sentences for (current item for playlists, or the content itself)
  const sentenceContentId = isPlaylist ? currentItem?.id : content?.id;
  const isNowPlaying =
    !!sentenceContentId && sentenceContentId === nowPlayingId;

  useEffect(() => {
    sentencesRef.current = sentences;
  }, [sentences]);

  const currentTimeRef = useRef(currentTime);
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  const timeMapRef = useRef(timeMap);
  useEffect(() => {
    timeMapRef.current = timeMap;
  }, [timeMap]);

  // Fetch content (wait for auth to complete first)
  useEffect(() => {
    if (!id || authLoading) return;
    fetch(`/api/content/${id}`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch content');
        return res.json();
      })
      .then((data) => setContent(data))
      .catch((err) => {
        console.error('Failed to fetch content:', err);
      });
  }, [id, authLoading]);

  // Reset all data when id changes
  useEffect(() => {
    setContent(null);
    setSentences([]);
    setTimeMap([]);
    setLoading(true);
    setSentencesFetched(false);
    setLoadingMore(false);
    setPlaylistItems([]);
  }, [id]);

  // Fetch playlist items when content is a playlist
  useEffect(() => {
    if (!content || content.type !== 'playlist') {
      setPlaylistItems([]);
      return;
    }

    let ids: string[] = [];
    try {
      const parsed = JSON.parse(content.contentIds || '[]');
      ids = Array.isArray(parsed) ? parsed : [];
    } catch {
      ids = [];
    }

    if (ids.length === 0) {
      setPlaylistItems([]);
      return;
    }

    let cancelled = false;
    fetch('/api/content/batch', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((items: ListData[]) => {
        if (!cancelled) setPlaylistItems(items);
      })
      .catch(() => {
        if (!cancelled) setPlaylistItems([]);
      });

    return () => {
      cancelled = true;
    };
  }, [content]);

  // Set playlist position
  const setPlaylistPosition = useCallback(
    async (position: number) => {
      if (!content || content.type !== 'playlist') return;

      // If this playlist is currently playing, use player
      if (currentPlaylist?.id === content.id) {
        playPlaylist({ ...content, playlistPosition: position });
      } else {
        // Update on server
        await fetch('/api/queue/add', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contentId: content.id,
            playlistPosition: position,
          }),
        });
        // Update local state
        setContent({ ...content, playlistPosition: position });
      }
    },
    [content, currentPlaylist, playPlaylist]
  );

  // Always fetch sentences - unified view shows transcript by default
  const needsSentences = true;

  // Reset sentences when the content we need sentences for changes
  useEffect(() => {
    setSentences([]);
    setTimeMap([]);
    setSentencesFetched(false);
  }, [sentenceContentId]);

  // Fetch sentences once when first needed (wait for auth)
  // For playlists, fetch sentences for the current item
  useEffect(() => {
    if (!sentenceContentId || authLoading) return;
    if (!needsSentences) {
      setLoading(false);
      return;
    }
    // Already fetched for this content
    if (sentencesFetched) {
      setLoading(false);
      return;
    }

    const fetchAllSentences = async () => {
      setLoading(true);
      try {
        const initialRes = await fetch(
          `/api/sentences/${sentenceContentId}?start=0&end=${FETCH_BATCH}`,
          { credentials: 'include' }
        );
        if (!initialRes.ok) throw new Error('Failed to fetch');

        const initialData = await initialRes.json();
        setTimeMap(initialData.timeMap);

        // Render first batch immediately
        setSentences(initialData.sentences);
        setLoading(false);

        if (initialData.total <= FETCH_BATCH) {
          // If no sentences yet (content still parsing), don't mark as fetched
          // so the effect re-runs when content status changes.
          if (initialData.total === 0 && content?.status === 'pending') return;
          setSentencesFetched(true);
          return;
        }

        // Fetch remaining batches progressively
        setLoadingMore(true);
        let start = FETCH_BATCH;

        while (start < initialData.total) {
          const res = await fetch(
            `/api/sentences/${sentenceContentId}?start=${start}&end=${Math.min(start + FETCH_BATCH, initialData.total)}`,
            { credentials: 'include' }
          );
          if (res.ok) {
            const data = await res.json();
            // Append new sentences as they arrive
            setSentences((prev) => {
              const combined = [...prev, ...data.sentences];
              return combined.sort((a, b) => a.idx - b.idx);
            });
            if (data.timeMap) setTimeMap(data.timeMap);
          }
          start += FETCH_BATCH;
        }

        setSentencesFetched(true);
        setLoadingMore(false);
      } catch (err) {
        console.error('Failed to fetch sentences', err);
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchAllSentences();
    // content?.status included so we re-fetch when pending→paused (sentences become available)
  }, [
    sentenceContentId,
    needsSentences,
    sentencesFetched,
    authLoading,
    content?.status,
  ]);

  // While listening, keep polling until transcript for the current time exists.
  // This handles:
  // - content still parsing (sentences being added over time)
  // - playlists where we start playback before transcript finished loading
  // - "fire-and-forget" transcript availability: we keep trying until the sentence exists
  //   and has word timings (when available).
  const lastTranscriptPollAtRef = useRef(0);
  useEffect(() => {
    if (!sentenceContentId || authLoading) return;
    if (!isNowPlaying || !isPlaying) return;

    let stopped = false;

    const pollOnce = async () => {
      if (stopped) return;
      // Don't poll when page is hidden (iOS can play audio in background,
      // but network requests may block the audio stream)
      if (document.hidden) return;

      const now = Date.now();
      if (now - lastTranscriptPollAtRef.current < TRANSCRIPT_POLL_COOLDOWN_MS) {
        return;
      }
      lastTranscriptPollAtRef.current = now;

      const currentSentences = sentencesRef.current;
      const currentTotal = currentSentences.length;
      const tNow = currentTimeRef.current;

      // If we have no transcript yet, keep polling the initial batch.
      if (currentTotal === 0) {
        try {
          const res = await fetch(
            `/api/sentences/${sentenceContentId}?start=0&end=${FETCH_BATCH}`,
            { credentials: 'include' }
          );
          if (!res.ok) return;
          const data = await res.json();
          if (stopped) return;
          if (data.timeMap) setTimeMap(data.timeMap);
          if (Array.isArray(data.sentences) && data.sentences.length > 0) {
            setSentences(data.sentences);
          }
        } catch {
          // ignore
        }
        return;
      }

      // Map current playback time to a sentence index.
      const tm = timeMapRef.current;
      let idx = 0;
      if (tm.length > 0) {
        let lo = 0,
          hi = tm.length - 1;
        while (lo < hi) {
          const mid = Math.ceil((lo + hi + 1) / 2);
          const midTime = tm[mid];
          if (midTime !== undefined && midTime <= tNow) lo = mid;
          else hi = mid - 1;
        }
        idx = lo;
      } else {
        // No timeMap yet; fall back to last known sentence.
        idx = Math.max(0, currentTotal - 1);
      }

      // If the sentence for the current time hasn't been loaded yet, fetch more.
      if (idx >= currentTotal) {
        try {
          const res = await fetch(
            `/api/sentences/${sentenceContentId}?start=${currentTotal}&end=${currentTotal + FETCH_BATCH}`,
            { credentials: 'include' }
          );
          if (!res.ok) return;
          const data = await res.json();
          if (stopped) return;
          if (data.timeMap) setTimeMap(data.timeMap);
          if (Array.isArray(data.sentences) && data.sentences.length > 0) {
            setSentences((prev) => {
              const combined = [...prev, ...data.sentences];
              return combined.sort((a, b) => a.idx - b.idx);
            });
          }
        } catch {
          // ignore
        }
        return;
      }

      // If the current sentence exists but has no word timings yet, keep polling just that sentence.
      const currentSentence = currentSentences[idx];
      if (
        currentSentence &&
        (!currentSentence.words || currentSentence.words.length === 0)
      ) {
        try {
          const res = await fetch(
            `/api/sentences/${sentenceContentId}?start=${idx}&end=${idx + 1}`,
            { credentials: 'include' }
          );
          if (!res.ok) return;
          const data = await res.json();
          if (stopped) return;
          if (data.timeMap) setTimeMap(data.timeMap);
          const update = (data.sentences as ParsedSentence[] | undefined)?.[0];
          if (update?.idx === idx && update.words?.length) {
            setSentences((prev) =>
              prev.map((s) =>
                s.idx === idx ? { ...s, words: update.words } : s
              )
            );
          }
        } catch {
          // ignore
        }
      }
    };

    // Immediate poll and then interval while playing
    pollOnce();
    const interval = setInterval(pollOnce, TRANSCRIPT_POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [sentenceContentId, authLoading, isNowPlaying, isPlaying]);

  // Poll for updates when content is processing
  useEffect(() => {
    if (!content) return;
    if (content.status !== 'processing' && content.status !== 'pending') return;

    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(`/api/content/${content.id}`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setContent(data);
          if (data.status !== 'processing' && data.status !== 'pending') {
            clearInterval(pollInterval);
          }
        }
      } catch (err) {
        console.error('Failed to poll content', err);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [content?.status, content?.id]);

  // Short-burst poll for post-processing metadata after pending/processing -> paused
  // Avoids constant polling (mobile background tabs).
  useEffect(() => {
    if (!content) return;

    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = content.status || null;

    const transitionedToPaused =
      (prevStatus === 'pending' || prevStatus === 'processing') &&
      content.status === 'paused';

    if (!transitionedToPaused) return;
    if (content.description) return;

    let stopped = false;
    const startedAt = Date.now();

    const interval = setInterval(async () => {
      if (stopped) return;

      if (Date.now() - startedAt > 10_000) {
        clearInterval(interval);
        stopped = true;
        return;
      }

      try {
        const res = await fetch(`/api/content/${content.id}`, {
          credentials: 'include',
        });
        if (!res.ok) return;

        const data = await res.json();
        setContent(data);

        if (data.description) {
          clearInterval(interval);
          stopped = true;
        }
      } catch (err) {
        console.error('Failed to poll post-processing metadata', err);
      }
    }, 1000);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [content?.id, content?.status, content?.description]);

  // Refresh word-level timestamps while generating and shortly after.
  // Polls every 5s during processing, then 20s after generation ends
  // to catch fire-and-forget STT results.
  useEffect(() => {
    if (!id || !content || !sentencesFetched) return;
    if (content.type !== 'content') return;

    const isProcessing = content.status === 'processing';
    const prev = prevWordRefreshStatusRef.current;
    prevWordRefreshStatusRef.current = content.status || null;

    // Start polling if processing, or if just transitioned from processing
    const justFinished =
      prev === 'processing' &&
      content.status !== 'processing' &&
      content.status !== 'pending';

    if (!isProcessing && !justFinished) return;

    let stopped = false;
    const startedAt = Date.now();
    const POLL_INTERVAL = isProcessing ? 5000 : 2500;
    const POST_GEN_DURATION = 20_000; // poll 20s after generation ends

    const fetchWords = async () => {
      const total = sentencesRef.current.length;
      if (!total) return;
      try {
        const res = await fetch(`/api/sentences/${id}?start=0&end=${total}`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        const updates = data?.sentences as ParsedSentence[] | undefined;
        if (!updates?.length) return;

        setSentences((prevSentences) => {
          if (!prevSentences.length) return prevSentences;
          const byIdx = new Map<number, ParsedSentence>();
          for (const s of updates) {
            if (s?.idx === undefined) continue;
            if (!s.words?.length) continue;
            byIdx.set(s.idx, s);
          }
          if (!byIdx.size) return prevSentences;
          return prevSentences.map((s) => {
            const updated = byIdx.get(s.idx);
            if (!updated) return s;
            return { ...s, words: updated.words };
          });
        });
        if (data.timeMap) setTimeMap(data.timeMap);
      } catch {
        // ignore
      }
    };

    // Fetch immediately on transition, then poll
    if (justFinished) fetchWords();

    const interval = setInterval(() => {
      if (stopped) return;
      // For post-generation polling, stop after duration
      if (!isProcessing && Date.now() - startedAt > POST_GEN_DURATION) {
        clearInterval(interval);
        return;
      }
      fetchWords();
    }, POLL_INTERVAL);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [id, content?.status, content?.type, sentencesFetched]);

  // Loading state - no content yet
  if (!content) {
    return <div className="page" />;
  }

  const value: ItemContextValue = {
    content,
    sentences: loading ? [] : sentences,
    timeMap,
    selection,
    setSelection,
    clearSelection,
    hasSelection,
    loading,
    loadingMore,
    // Playlist fields
    isPlaylist,
    playlistItems,
    currentPosition,
    currentItem,
    setPlaylistPosition,
  };

  // For pending/failed content or playlists, use simpler wrapper
  if (
    content.type === 'playlist' ||
    content.status === 'pending' ||
    content.status === 'failed'
  ) {
    return (
      <ItemContext.Provider value={value}>
        <div
          className="page"
          style={{
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {children}
        </div>
      </ItemContext.Provider>
    );
  }

  return (
    <ItemContext.Provider value={value}>
      <div
        className="page"
        style={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {children}
      </div>
    </ItemContext.Provider>
  );
}
